import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { useRequireHistoricoCliente } from '@/hooks/useAgendamentoFlags'
import { useToast } from '@/contexts/ToastContext'
import { getAgendamentos, getCompras, cancelarAgendamento } from '@/api/usuariosApi'
import type { AgendamentoResumo, CompraResumo } from '@/api/usuariosApi'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { GuestClientPanel } from '@/components/GuestClientPanel'
import { isGuestClient } from '@/utils/guestAuth'
import styles from './HistoricoPage.module.css'

function formatarData(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
  const mes = meses[Number(m) - 1]
  return `${Number(d)} ${mes} ${y}`
}

function statusLabel(status: AgendamentoResumo['status']): string {
  if (status === 'agendado') return 'Agendado'
  if (status === 'realizado') return 'Realizado'
  return 'Cancelado'
}

export function HistoricoPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('agendamento')
  const { loading: featureLoading } = useRequireHistoricoCliente()
  const { user } = useUser()
  const { toast } = useToast()
  const [agendamentos, setAgendamentos] = useState<AgendamentoResumo[]>([])
  const [compras, setCompras] = useState<CompraResumo[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [cancelandoId, setCancelandoId] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<AgendamentoResumo | null>(null)
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const load = useCallback(() => {
    if (!user) {
      setLoading(false)
      return
    }
    setLoading(true)
    setErro(null)
    Promise.all([getAgendamentos(), getCompras()])
      .then(([ags, comprasList]) => {
        setAgendamentos(ags)
        setCompras(comprasList)
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Erro ao carregar'
        setErro(msg)
        toast(msg, 'error')
      })
      .finally(() => setLoading(false))
  }, [user, toast])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!highlightId || loading) return
    const el = cardRefs.current[highlightId]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [highlightId, loading, agendamentos])

  const confirmCancel = async () => {
    if (!user || !cancelTarget) return
    setCancelandoId(cancelTarget.id)
    try {
      await cancelarAgendamento(Number(cancelTarget.id))
      toast('Agendamento cancelado.', 'success')
      setCancelTarget(null)
      load()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Erro ao cancelar', 'error')
    } finally {
      setCancelandoId(null)
    }
  }

  const handleRemarcar = (ag: AgendamentoResumo) => {
    navigate('/agendamento', {
      state: {
        reagendar: {
          agendamentoId: Number(ag.id),
          profissional: ag.profissional,
          unidade: ag.unidade,
          servico_id: ag.servico_id ?? null,
        },
      },
    })
  }

  if (featureLoading) return null

  if (isGuestClient(user)) {
    return (
      <main className={`${styles.page} animate-slide-up`}>
        <button type="button" className={styles.backLink} onClick={() => navigate('/menu')}>
          ← Voltar
        </button>
        <div className={styles.wrapper}>
          <h1 className={styles.heading}>Histórico</h1>
          <GuestClientPanel variant="historico" />
        </div>
      </main>
    )
  }

  return (
    <main className={`${styles.page} animate-slide-up`}>
      <button type="button" className={styles.backLink} onClick={() => navigate('/menu')}>
        ← Voltar
      </button>

      <div className={styles.wrapper}>
        <h1 className={styles.heading}>Histórico</h1>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Agendamentos</h2>
          {loading ? (
            <div className={styles.empty}>A carregar…</div>
          ) : erro ? (
            <div className={styles.empty} style={{ color: 'var(--danger)' }}>
              {erro}
            </div>
          ) : agendamentos.length > 0 ? (
            <div className={styles.list}>
              {agendamentos.map((ag, i) => {
                const highlighted = highlightId === ag.id
                return (
                  <div
                    key={ag.id}
                    ref={(el) => {
                      cardRefs.current[ag.id] = el
                    }}
                    className={`${styles.cardItem} ${highlighted ? styles.cardHighlight : ''}`}
                    style={{ animationDelay: `${i * 0.06}s` }}
                  >
                    <div className={styles.cardItemTop}>
                      <span className={styles.cardItemTitle}>
                        {ag.profissional} · {ag.hora}
                      </span>
                      <span
                        className={
                          ag.status === 'cancelado'
                            ? styles.badgeCancelado
                            : ag.status === 'agendado'
                              ? styles.badgeAgendado
                              : styles.badgeRealizado
                        }
                      >
                        {statusLabel(ag.status)}
                      </span>
                    </div>
                    <div className={styles.cardItemMeta}>
                      <span>{formatarData(ag.data)}</span>
                      <span>{ag.unidade}</span>
                      <span>{ag.servico_nome || 'Serviço não indicado'}</span>
                    </div>
                    {ag.status === 'agendado' && (
                      <div className={styles.cardActions}>
                        <button
                          type="button"
                          className={styles.btnRemarcar}
                          onClick={() => handleRemarcar(ag)}
                        >
                          Remarcar
                        </button>
                        <button
                          type="button"
                          className={styles.btnCancelar}
                          disabled={cancelandoId === ag.id}
                          onClick={() => setCancelTarget(ag)}
                        >
                          {cancelandoId === ag.id ? 'A cancelar…' : 'Cancelar'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={styles.empty}>Nenhum agendamento registado.</div>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Serviços e pacotes comprados</h2>
          {loading ? (
            <div className={styles.empty}>A carregar…</div>
          ) : compras.length > 0 ? (
            <div className={styles.list}>
              {compras.map((c, i) => (
                <div key={c.id} className={styles.cardItem} style={{ animationDelay: `${i * 0.06}s` }}>
                  <div className={styles.cardItemTop}>
                    <span className={styles.cardItemTitle}>{c.pacote_nome}</span>
                    <span className={styles.badgeRealizado}>
                      {c.status === 'pago_local' ? 'Pago no local' : c.status}
                    </span>
                  </div>
                  <div className={styles.cardItemMeta}>
                    <span>{formatarData(c.data_compra)}</span>
                    <span>
                      {c.metodo_pagamento === 'fisico_local' ? 'Pagamento no local' : c.metodo_pagamento}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              Nenhum pacote comprado. Contrate um pacote na página Pacotes.
            </div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancelar agendamento?"
        message={
          cancelTarget
            ? `${formatarData(cancelTarget.data)} às ${cancelTarget.hora} · ${cancelTarget.profissional}`
            : undefined
        }
        confirmLabel="Sim, cancelar"
        cancelLabel="Voltar"
        danger
        loading={!!cancelTarget && cancelandoId === cancelTarget.id}
        onConfirm={confirmCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </main>
  )
}
